import { dateToDateString } from '~/components/utils/dateUtils';
import type { GlobalFiltersSelection } from '~/contexts/globalFilterContext';

// The only search params the global filter bar owns. Everything else in the URL
// belongs to some other surface and must survive a filter change.
export const GLOBAL_FILTER_PARAM_KEYS = [
  'portfolio',
  'service_provider',
  'region',
  'start_date',
  'end_date',
] as const;

/**
 * Rewrite the global-filter params on a *copy* of `params`, leaving every other
 * param untouched.
 *
 * Rebuilding the params from scratch is tempting but wrong: the URL also carries
 * params the filter bar knows nothing about, including the ones
 * `DetailParamRedirects` turns into a navigation to a record's page
 * (`selectedInvoiceId`, `selectedContractId`/`editingContractId`,
 * `selectedCarId`/`carPanelTab`, and `carId`/`car_panel` on the Elevators page).
 * Dropping those loses whatever record the user was about to be taken to the
 * moment they touch a filter.
 */
export function withGlobalFilters(
  params: URLSearchParams,
  filters: GlobalFiltersSelection,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of GLOBAL_FILTER_PARAM_KEYS) {
    next.delete(key);
  }

  for (const portfolio of filters.portfolios) {
    next.append('portfolio', portfolio);
  }
  for (const serviceProvider of filters.serviceProviders) {
    next.append('service_provider', serviceProvider);
  }
  for (const region of filters.regions) {
    next.append('region', region);
  }
  next.set('start_date', dateToDateString(filters.date.start));
  next.set('end_date', dateToDateString(filters.date.end));

  return next;
}

/**
 * Copy the global-filter params from `from` onto a copy of `target`, leaving any the
 * target sets itself alone.
 *
 * For links built away from the current URL rather than derived from it — the command
 * palette's results are the only ones today. Each names just the record it points at,
 * so following one dropped the reader's portfolio and period, and the app shell then
 * bounced the destination through its default-range redirect.
 */
export function carryGlobalFilters(
  target: URLSearchParams,
  from: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(target);
  for (const key of GLOBAL_FILTER_PARAM_KEYS) {
    if (next.has(key)) continue;
    for (const value of from.getAll(key)) {
      next.append(key, value);
    }
  }

  return next;
}

/**
 * What to send the API for one global-filter dropdown, or `undefined` when the control
 * reads as "All".
 *
 * `SelectCheckbox` shows "All" both for an empty selection and for a select-everything,
 * so the two have to behave the same here. On the Work Queue they otherwise wouldn't:
 * Portfolio and Region join through `property`, and AI-pipeline items have none until
 * the document is matched, so sending every id would quietly drop them.
 *
 * Stale ids still sitting in the URL are dropped so they can't narrow a list to nothing.
 */
export function appliedGlobalFilter(
  selected: string[],
  options: { id: string }[],
): string[] | undefined {
  const known = selected.filter((id) => options.some((option) => option.id === id));
  if (known.length === 0 || known.length === options.length) return undefined;
  return known;
}
